import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClerkProvider } from "@clerk/react";
import { dark } from "@clerk/ui/themes";
import { useTheme } from "@/lib/theme";

// Brand Clerk here once: it styles sign-in, sign-up and the user menu. Dark app: `yarn add @clerk/ui@1.38.1`,
// `import { dark } from "@clerk/ui/themes"`, then add `theme: dark, captcha: { theme: "dark" }` (see Styling).
const appearance = {
  variables: {
    colorPrimary: "#C2410C",
    colorPrimaryForeground: "#ffffff",
    fontFamily: "IBM Plex Sans, sans-serif",
    borderRadius: "0.6rem",
  },
};

const localization = {
  locale: "de-DE",
  formButtonPrimary: "Weiter",
  formFieldLabel__emailAddress: "E-Mail-Adresse",
  formFieldLabel__password: "Passwort",
  formFieldInputPlaceholder__emailAddress: "E-Mail-Adresse eingeben",
  formFieldInputPlaceholder__password: "Passwort eingeben",
  formFieldAction__forgotPassword: "Passwort vergessen?",
  signIn: {
    start: { title: "Anmelden bei {{applicationName}}", titleCombined: "Anmelden bei {{applicationName}}", subtitle: "Zugang nur mit vom Administrator angelegtem Konto.", subtitleCombined: "Zugang nur mit vom Administrator angelegtem Konto.", actionText: "", actionLink: "" },
    password: { title: "Passwort eingeben", subtitle: "Weiter zu {{applicationName}}", actionLink: "Andere Methode verwenden" },
    emailCode: { title: "E-Mail prüfen", subtitle: "Weiter zu {{applicationName}}", formTitle: "Bestätigungscode", resendButton: "Code erneut senden" },
  },
  signUp: { start: { title: "Konto erstellen", subtitle: "Weiter zu {{applicationName}}", actionText: "Bereits ein Konto?", actionLink: "Anmelden" } },
  userButton: { action__manageAccount: "Konto verwalten", action__signOut: "Abmelden" },
  unstable__errors: {
    form_identifier_not_found: "Kein Konto mit dieser E-Mail-Adresse. Bitte wenden Sie sich an den Administrator.",
    form_password_incorrect: "Das Passwort ist falsch.",
  },
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Starts Clerk from the backend's runtime config, so deploy's live keys need no rebuild.
// Must render inside <BrowserRouter> (see step 3) so Clerk navigates without reloading.
export default function ClerkAuthProvider({ children }) {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  const isDark = useTheme() === "dark";

  // Retries while the backend restarts (e.g. right after provisioning): the app's
  // Clerk components crash if they ever render without <ClerkProvider>.
  useEffect(() => {
    let cancelled = false;
    const load = async (attempt = 0) => {
      try {
        const cfg = await (await fetch(`${BACKEND_URL}/api/clerk/config`)).json();
        if (cfg.enabled) return cancelled || setConfig(cfg);
      } catch {}
      if (cancelled) return;
      if (attempt >= 20) return setUnavailable(true);
      setTimeout(() => load(attempt + 1), 1500);
    };
    load();
    return () => { cancelled = true; };
  }, []);

  if (unavailable) return <p style={{ padding: 24 }}>Anmeldung ist gerade nicht verfügbar. Bitte Seite neu laden.</p>;
  if (!config) return null;
  return (
    <ClerkProvider
      publishableKey={config.publishable_key}
      proxyUrl={config.proxy_url || undefined}
      appearance={isDark ? { ...appearance, theme: dark, captcha: { theme: "dark" }, variables: { ...appearance.variables, colorPrimary: "#EA580C", colorBackground: "#121A2A" } } : appearance}
      localization={localization}
      routerPush={(to) => navigate(to)}
      routerReplace={(to) => navigate(to, { replace: true })}
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
      signInFallbackRedirectUrl="/"
      signUpFallbackRedirectUrl="/"
      afterSignOutUrl="/"
    >
      {children}
    </ClerkProvider>
  );
}
